// Advisor performance — deterministic counting only, no AI involved anywhere in this
// file (docs/analytics-dashboard-strategy.md §4.4). Every number here is a plain count
// or average over real rows from `groups`, `feedback`, and `message_log`.
//
// On-time delivery is deliberately not computed here: `groups.actual_delivered_at` has
// never been written to (checked live), so there is no honest way to measure it yet —
// see docs/backend-integration.md item 8a.

import { STEP_DEFS, nb } from "./journey";
import { parseRating } from "./feedback";

const normalizeName = (v) => String(v || "").trim().toLowerCase();

// Plain workshop-word nouns for each step, so a reason reads as "Estimate missing on 3
// of 10 job cards" rather than repeating the step's instruction-style label verbatim.
const STEP_NOUN = {
  created: "Group creation",
  invites: "Invitation link",
  initial: "Escalation metrics",
  media: "Inventory photos",
  estimate: "Estimate",
  jobcard: "Job card",
  progress: "Work-in-progress update",
  parts_approval: "Parts approval",
  parts: "Parts recommendation",
  pre_invoice: "Pre-invoice",
  invoice: "Invoice",
  vehicle_delivered: "Delivery notice",
  pre_delivery_feedback: "Pre delivery feedback",
  feedback: "Feedback",
};

// A reply taking longer than this is what the reason lines call out by name — same
// "2 hours" example already used in the strategy doc's own voice examples.
const SLOW_REPLY_SECONDS = 2 * 60 * 60;
// Plain, stated assumption for the Healthy / Needs Attention cutoffs below — not tuned
// against anything, just a reasonable workshop target.
const ADHERENCE_TARGET_PCT = 80;
const CSAT_TARGET_PCT = 70;
const REPLY_TARGET_MINS = 30;

function median(nums) {
  if (!nums.length) return null;
  const sorted = [...nums].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

function stepCompletionFor(groupsSubset) {
  return STEP_DEFS.map((step) => {
    const applicable = groupsSubset.filter((g) => (step.show ? step.show(g) : true));
    const missingRowIds = [];
    let done = 0;
    applicable.forEach((g) => {
      if (step.done(g)) done++;
      else missingRowIds.push(g._row);
    });
    return { stepKey: step.key, label: step.label, done, total: applicable.length, missingRowIds };
  });
}

// csatEntries/replyEntries carry the group row id alongside each rating/reply-time so a
// reason can point back to exactly the job cards behind it (used to jump into Service
// Journey pre-filtered to just those groups — see ReasonRow's onClick).
function reasonsFor(stepCompletion, csatEntries, replyEntries) {
  const reasons = stepCompletion
    .filter((s) => s.total > 0 && s.done < s.total)
    .map((s) => ({
      text: `${STEP_NOUN[s.stepKey] || s.label} missing on ${s.total - s.done} of ${s.total} job cards`,
      count: s.total - s.done,
      total: s.total,
      groupRowIds: s.missingRowIds,
    }))
    .sort((a, b) => b.count - a.count);

  if (csatEntries.length) {
    const low = csatEntries.filter((e) => e.rating <= 3);
    if (low.length > 0) {
      reasons.push({
        text: `${low.length} of ${csatEntries.length} customers rated their experience 3 or below`,
        count: low.length,
        total: csatEntries.length,
        groupRowIds: [...new Set(low.map((e) => e.rowId))],
      });
    }
  }

  if (replyEntries.length) {
    const slow = replyEntries.filter((e) => e.seconds > SLOW_REPLY_SECONDS);
    if (slow.length > 0) {
      reasons.push({
        text: `Customers waited over 2 hours for a reply on ${slow.length} of ${replyEntries.length} measured replies`,
        count: slow.length,
        total: replyEntries.length,
        groupRowIds: [...new Set(slow.map((e) => e.rowId))],
      });
    }
  }

  return reasons;
}

function adherencePctFor(stepCompletion) {
  const totals = stepCompletion.reduce(
    (acc, s) => ({ done: acc.done + s.done, total: acc.total + s.total }),
    { done: 0, total: 0 }
  );
  return totals.total ? Math.round((totals.done / totals.total) * 100) : null;
}

function csatPctFor(ratings) {
  return ratings.length ? Math.round((ratings.filter((n) => n >= 4).length / ratings.length) * 100) : null;
}

function stateFor(adherencePct, csatPct, medianResponseMins) {
  const needsAttention =
    (adherencePct != null && adherencePct < ADHERENCE_TARGET_PCT) ||
    (csatPct != null && csatPct < CSAT_TARGET_PCT) ||
    (medianResponseMins != null && medianResponseMins > REPLY_TARGET_MINS);
  return needsAttention ? "Needs Attention" : "Healthy";
}

// Builds the per-group lookups once, shared by both per-advisor and per-workshop calls.
function buildIndexes(groups, feedbackRows, messageLogRows) {
  const groupsByRow = new Map(groups.map((g) => [g._row, g]));

  const feedbackByGroupRow = new Map();
  feedbackRows.forEach((f) => {
    const rowId = f["Group Row Id"];
    if (rowId == null) return;
    if (!feedbackByGroupRow.has(rowId)) feedbackByGroupRow.set(rowId, []);
    feedbackByGroupRow.get(rowId).push(f);
  });

  const repliesByGroupRow = new Map();
  messageLogRows.forEach((m) => {
    if (m.response_seconds == null || m.group_row_id == null) return;
    if (!groupsByRow.has(m.group_row_id)) return; // message belongs to a group outside this fetch
    if (!repliesByGroupRow.has(m.group_row_id)) repliesByGroupRow.set(m.group_row_id, []);
    repliesByGroupRow.get(m.group_row_id).push(m.response_seconds);
  });

  return { feedbackByGroupRow, repliesByGroupRow };
}

function metricsForGroupSet(groupsSubset, feedbackByGroupRow, repliesByGroupRow) {
  const stepCompletion = stepCompletionFor(groupsSubset);

  const csatEntries = [];
  const replyEntries = [];
  groupsSubset.forEach((g) => {
    (feedbackByGroupRow.get(g._row) || []).forEach((f) => {
      const r = parseRating(f["Overall Performance Rating"]);
      if (r != null) csatEntries.push({ rowId: g._row, rating: r });
    });
    (repliesByGroupRow.get(g._row) || []).forEach((seconds) => {
      replyEntries.push({ rowId: g._row, seconds });
    });
  });

  const adherencePct = adherencePctFor(stepCompletion);
  const csatPct = csatPctFor(csatEntries.map((e) => e.rating));
  const medianResponseMins = replyEntries.length ? median(replyEntries.map((e) => e.seconds)) / 60 : null;
  const jobCardsClosed = groupsSubset.filter((g) => nb(g["Feedback Notified"])).length;

  return {
    jobCardsClosed,
    jobCardsTotal: groupsSubset.length,
    adherencePct,
    csatPct,
    medianResponseMins,
    stepCompletion,
    reasons: reasonsFor(stepCompletion, csatEntries, replyEntries),
  };
}

// One row per advisor, grouped case-insensitively since the same person's name is
// sometimes typed differently across job cards (seen live: "GANESH" / "Ganesh").
export function buildAdvisorMetrics(groups, feedbackRows, messageLogRows) {
  const { feedbackByGroupRow, repliesByGroupRow } = buildIndexes(groups, feedbackRows, messageLogRows);

  const byAdvisor = new Map();
  groups.forEach((g) => {
    const rawName = g["Service Advisor Name"];
    if (!nb(rawName)) return;
    const key = normalizeName(rawName);
    if (!byAdvisor.has(key)) {
      byAdvisor.set(key, { displayName: String(rawName).trim(), workshopLocation: g["Workshop Location"] || "", groups: [] });
    }
    byAdvisor.get(key).groups.push(g);
  });

  const advisors = [];
  let id = 1;
  byAdvisor.forEach((entry) => {
    const m = metricsForGroupSet(entry.groups, feedbackByGroupRow, repliesByGroupRow);
    advisors.push({
      id: id++,
      advisorName: entry.displayName,
      workshopLocation: entry.workshopLocation,
      state: stateFor(m.adherencePct, m.csatPct, m.medianResponseMins),
      ...m,
    });
  });
  return advisors;
}
